import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CupetAllocationStatus,
  type CupetAllocationSummary,
  type CupetFuelType,
  type CupetServicenter,
} from '@tucupet/shared';
import { PageHeader, Table, Select, Field, Pager, Btn, Modal, StatCard } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { ConsumptionBar, StatusBadge } from './cupet-shared';

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: String(CupetAllocationStatus.INSERTED), label: 'Insertada' },
  { value: String(CupetAllocationStatus.IN_PROCESS), label: 'En proceso' },
  { value: String(CupetAllocationStatus.PROVISIONED), label: 'Aprovisionada' },
  { value: String(CupetAllocationStatus.CANCELLED), label: 'Cancelada' },
];

interface ListResp {
  items: CupetAllocationSummary[];
  total: number;
  page: number;
  limit: number;
}

const empty = {
  identifyProvider: '',
  nameProvider: '',
  phoneProvider: '',
  mailProvider: '',
  fuelTypeId: '',
  servicenterId: '',
  amount: '',
  amountPaid: '',
  currency: '',
  userId: '',
};

export default function CupetPage() {
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const status = sp.get('status') ?? '';
  const search = sp.get('search') ?? '';
  const page = parseInt(sp.get('page') ?? '1', 10);
  const [showAssign, setShowAssign] = React.useState(false);

  const mode = useQuery<{ mode: 'live' | 'mock' }>({
    queryKey: ['admin', 'cupet', 'mode'],
    queryFn: () => api('/api/admin/cupet/mode'),
    staleTime: 5 * 60_000,
  });

  const list = useQuery<ListResp>({
    queryKey: ['admin', 'cupet', 'list', status, search, page],
    queryFn: () =>
      api('/api/admin/cupet/allocations', {
        query: { status: status || undefined, search: search || undefined, page },
      }),
  });

  const setFilter = (next: Partial<{ status: string; search: string; page: string }>) => {
    const cur = { status, search, page: String(page) };
    const merged: Record<string, string> = {};
    for (const k of Object.keys(cur) as (keyof typeof cur)[]) {
      const v = next[k] !== undefined ? next[k] : cur[k];
      if (v && !(k === 'page' && v === '1')) merged[k] = v as string;
    }
    setSp(merged);
  };

  return (
    <div>
      <PageHeader
        kicker="COMBUSTIBLE CUPET"
        title={`Asignaciones · ${list.data?.total ?? 0}`}
        sub="Asigna combustible a clientes (vía API de CUPET) y controla su consumo."
        action={<Btn onClick={() => setShowAssign(true)}>+ Asignar combustible</Btn>}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard label="Conexión" value={mode.data ? (mode.data.mode === 'live' ? 'CUPET en vivo' : 'Simulada (mock)') : '…'} accent={mode.data?.mode === 'live'} />
        <StatCard label="Asignaciones" value={String(list.data?.total ?? 0)} />
        <Link to="/cupet/stock" style={{ textDecoration: 'none' }}>
          <StatCard label="Stock distribuidor" value="Ver →" />
        </Link>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 200 }}>
          <Select label="Estado" value={status} onChange={(v) => setFilter({ status: v, page: '1' })} options={STATUS_OPTIONS} />
        </div>
        <div style={{ minWidth: 260, flex: 1 }}>
          <Field label="Buscar" value={search} onChange={(v) => setFilter({ search: v, page: '1' })} placeholder="ID, NIT/CI o nombre" />
        </div>
      </div>

      <Table<CupetAllocationSummary>
        rows={list.data?.items ?? []}
        empty={list.isLoading ? 'Cargando…' : 'Sin asignaciones todavía'}
        columns={[
          {
            key: 'externalId',
            label: 'ID',
            render: (r) => (
              <Link to={`/cupet/${r.externalId}`} className="mono" style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>
                {r.externalId}
              </Link>
            ),
          },
          {
            key: 'cliente',
            label: 'Cliente',
            render: (r) => (
              <div>
                <div>{r.nameProvider || <span style={{ color: 'var(--text-3)' }}>—</span>}</div>
                <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)' }}>{r.identifyProvider}</div>
              </div>
            ),
          },
          { key: 'fuel', label: 'Combustible', render: (r) => <span className="mono" style={{ fontSize: 11 }}>#{r.fuelTypeId}</span> },
          { key: 'sc', label: 'Servicentro', render: (r) => <span className="mono" style={{ fontSize: 11 }}>#{r.servicenterId}</span> },
          {
            key: 'consumo',
            label: 'Consumo',
            width: 200,
            render: (r) => <ConsumptionBar amount={r.amount} realAmount={r.realAmount} />,
          },
          { key: 'status', label: 'Estado', render: (r) => <StatusBadge status={r.status} /> },
          { key: 'date', label: 'Fecha', render: (r) => <span className="mono" style={{ fontSize: 11, color: 'var(--text-3)' }}>{new Date(r.createdAt).toLocaleString('es-MX')}</span> },
        ]}
      />

      {list.data && <Pager total={list.data.total} page={list.data.page} limit={list.data.limit} onPage={(p) => setFilter({ page: String(p) })} />}

      <AssignModal
        open={showAssign}
        onClose={() => setShowAssign(false)}
        onAssigned={() => {
          setShowAssign(false);
          qc.invalidateQueries({ queryKey: ['admin', 'cupet', 'list'] });
        }}
      />
    </div>
  );
}

function AssignModal({ open, onClose, onAssigned }: { open: boolean; onClose: () => void; onAssigned: () => void }) {
  const [form, setForm] = React.useState({ ...empty });
  const set = (k: keyof typeof empty) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  const fuels = useQuery<{ items: CupetFuelType[] }>({
    queryKey: ['admin', 'cupet', 'fuel-types'],
    queryFn: () => api('/api/admin/cupet/fuel-types'),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const centers = useQuery<{ items: CupetServicenter[] }>({
    queryKey: ['admin', 'cupet', 'servicenters'],
    queryFn: () => api('/api/admin/cupet/servicenters'),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const assign = useMutation({
    mutationFn: () =>
      api<CupetAllocationSummary>('/api/admin/cupet/allocations', {
        body: {
          identifyProvider: form.identifyProvider.trim(),
          nameProvider: form.nameProvider.trim(),
          phoneProvider: form.phoneProvider.trim(),
          mailProvider: form.mailProvider.trim() || undefined,
          fuelTypeId: Number(form.fuelTypeId),
          servicenterId: Number(form.servicenterId),
          amount: Number(form.amount),
          amountPaid: form.amountPaid ? Number(form.amountPaid) : undefined,
          currency: form.currency.trim() || undefined,
          userId: form.userId.trim() || undefined,
        },
      }),
    onSuccess: () => {
      setForm({ ...empty });
      onAssigned();
    },
  });

  const fuelOptions = [{ value: '', label: 'Selecciona…' }, ...(fuels.data?.items ?? []).map((f) => ({ value: String(f.typeFuelId), label: `${f.typeFuelName} (#${f.typeFuelId})` }))];
  const centerOptions = [{ value: '', label: 'Selecciona…' }, ...(centers.data?.items ?? []).map((s) => ({ value: String(s.servicenterId), label: `${s.servicenterName} (#${s.servicenterId})` }))];

  const valid =
    form.identifyProvider.trim() && form.nameProvider.trim() && form.phoneProvider.trim() && form.fuelTypeId && form.servicenterId && Number(form.amount) > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Asignar combustible"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn onClick={() => assign.mutate()} disabled={!valid || assign.isPending}>{assign.isPending ? 'Asignando…' : 'Asignar'}</Btn>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Select label="Combustible" value={form.fuelTypeId} onChange={set('fuelTypeId')} options={fuelOptions} />
          <Select label="Servicentro" value={form.servicenterId} onChange={set('servicenterId')} options={centerOptions} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Cantidad (L)" type="number" step="0.01" value={form.amount} onChange={set('amount')} placeholder="0" />
          <Field label="NIT / CI" value={form.identifyProvider} onChange={set('identifyProvider')} placeholder="Identificador del cliente" />
        </div>
        <Field label="Nombre del cliente" value={form.nameProvider} onChange={set('nameProvider')} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Teléfono" value={form.phoneProvider} onChange={set('phoneProvider')} placeholder="+53…" />
          <Field label="Email (opcional)" value={form.mailProvider} onChange={set('mailProvider')} />
        </div>
        <details>
          <summary className="mono" style={{ fontSize: 11, color: 'var(--text-3)', cursor: 'pointer', letterSpacing: '0.08em' }}>OPCIONALES (PAGO / USUARIO)</summary>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
            <Field label="Monto pagado" type="number" step="0.01" value={form.amountPaid} onChange={set('amountPaid')} />
            <Field label="Moneda" value={form.currency} onChange={set('currency')} placeholder="CUP / USD" />
            <Field label="ID usuario" value={form.userId} onChange={set('userId')} placeholder="ObjectId (opcional)" />
          </div>
        </details>
        {assign.isError && (
          <div style={{ color: 'var(--danger)', fontSize: 12 }}>
            {(assign.error as ApiError)?.message ?? 'Error al asignar'}
            {Array.isArray((assign.error as ApiError)?.raw && (assign.error as any).raw?.errors)
              ? ` — ${((assign.error as any).raw.errors as string[]).join('; ')}`
              : ''}
          </div>
        )}
      </div>
    </Modal>
  );
}
